import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';

interface CreditPackage {
  credits: number;
  price: number;
  id: string;
}

const CREDIT_PACKAGES: CreditPackage[] = [
  { id: 'pack0', credits: 100, price: 15 },
  { id: 'pack1', credits: 350, price: 25 },
  { id: 'pack2', credits: 800, price: 35 },
  { id: 'pack3', credits: 1500, price: 50 },
];

const CreditsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  const [loading, setLoading] = useState(false);
  // Default selection: 1500 credits package (pack3)
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(
    CREDIT_PACKAGES.find(pkg => pkg.id === 'pack3') || null
  );

  React.useEffect(() => {
    if (!user) {
      navigate('/signin');
    }
  }, [user, navigate]);

  const handlePayment = async () => {
    if (!selectedPackage || !user) {
      alert(t('pages.credits.selectPackageFirst'));
      return;
    }
    
    setLoading(true);
    
    try {
      // Fetch user profile details (username, phone) if available
      const { data: profile } = await supabase
        .from('users')
        .select('username, phone_number')
        .eq('id', user.id)
        .maybeSingle();

      // 1. إنشاء عملية دفع جديدة في قاعدة البيانات
      const { data, error } = await supabase.rpc('create_payment_transaction', {
        p_user_id: user.id,
        p_package_id: selectedPackage.id,
        p_credits_amount: selectedPackage.credits,
        p_price_amount: selectedPackage.price,
        p_metadata: {
          package_name: selectedPackage.id,
          timestamp: new Date().toISOString()
        }
      });

      if (error) {
        console.error('Error creating transaction:', error);
        console.error('Error details:', error.message, error.hint);
        alert(`${t('pages.credits.errorCreating')}: ${error.message || t('pages.credits.errorUnknown')}`);
        setLoading(false);
        return;
      }

      console.log('Transaction data:', data);

      if (!data || !data.success || !data.transaction_number) {
        console.error('Invalid transaction data:', data);
        alert(t('pages.credits.errorTryAgain'));
        setLoading(false);
        return;
      }

      // 2. إنشاء رابط Tap عبر Webhook الخارجي (n8n)
      const createChargeResponse = await fetch('https://n8n.srv1094338.hstgr.cloud/webhook/f45913c6-8dcc-4516-rendercreateLink-d4b9dea208d4', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // بيانات ضرورية للويبهوك
          transaction_id: data.transaction_number,
          amount: selectedPackage.price,
          credits_amount: selectedPackage.credits,
          currency: 'USD',
          package_id: selectedPackage.id,
          redirect_url: `${window.location.origin}/payment-waiting?txn=${data.transaction_number}`,
          user: {
            id: user.id,
            email: user.email,
            name: profile?.username || user.email?.split('@')[0] || '',
            phone: profile?.phone_number || ''
          },
        })
      });

      const tapData = await createChargeResponse.json();
      console.log('Tap response:', tapData);

      if (!createChargeResponse.ok || (tapData.success === false)) {
        console.error('Tap Payment Error:', tapData);
        alert(`${t('pages.credits.errorPaymentGateway')}: ${tapData.error || t('pages.credits.errorUnknown')}`);
        setLoading(false);
        return;
      }

      // 3. حفظ معلومات Tap Payment
      if (tapData.charge_id) {
        await supabase.rpc('update_payment_status', {
          p_transaction_id: data.transaction_id,
          p_status: 'pending',
          p_tap_charge_id: tapData.charge_id
        });
      }

      // 4. إعادة التوجيه إلى صفحة الدفع
      const paymentUrl = tapData.payment_url || tapData.url || tapData.link;
      console.log('Redirecting to:', paymentUrl);
      
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        // إذا لم يكن هناك رابط، انتقل إلى صفحة الانتظار
        navigate(`/payment-waiting?txn=${data.transaction_number}`);
      }

    } catch (err) {
      console.error('Error:', err);
      alert(t('pages.credits.errorTryAgain'));
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t('pages.credits.title')}
          </h1>
          <p className="text-gray-400 text-lg mb-3">
            {t('pages.credits.subtitle')}
          </p>
          
          {/* Not Subscription Notice */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-2 border-green-500/40 rounded-full px-6 py-3 mt-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-300 font-bold text-sm sm:text-base">
              {t('pages.credits.notSubscription')}
            </span>
          </div>
        </div>

        {/* Credit Packages */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-12">
          {CREDIT_PACKAGES.map((pkg) => {
            const isSelected = selectedPackage?.id === pkg.id;
            const pricePerCredit = (pkg.price / pkg.credits).toFixed(3);
            
            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                className={`relative bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-xl sm:rounded-2xl p-3 sm:p-8 transition-all duration-300 hover:scale-105 ${
                  isSelected 
                    ? 'ring-2 sm:ring-4 ring-blue-500 shadow-xl sm:shadow-2xl shadow-blue-500/50' 
                    : 'hover:ring-2 hover:ring-blue-400/50'
                }`}
              >
                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 w-7 h-7 sm:w-10 sm:h-10 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}

                {/* Credits */}
                <div className="mb-3 sm:mb-6">
                  <div className="text-yellow-400 text-2xl sm:text-6xl font-bold mb-1 sm:mb-2">
                    {pkg.credits}
                  </div>
                  <div className="text-gray-400 text-[10px] sm:text-sm">{t('pages.credits.credits')}</div>
                </div>

                {/* Price */}
                <div className="mb-2 sm:mb-4">
                  <div className="text-white text-xl sm:text-4xl font-bold mb-1">
                    ${pkg.price}
                  </div>
                  <div className="text-gray-500 text-[9px] sm:text-xs hidden sm:block">
                    ${pricePerCredit} / {t('pages.credits.credits')}
                  </div>
                </div>

                {/* Best Value Badge */}
                {pkg.id === 'pack3' && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[9px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg">
                      {t('pages.credits.bestValue')}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Package Summary */}
        {selectedPackage && (
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/40 border-2 border-blue-500/50 rounded-2xl p-8 mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-center sm:text-right">
                <div className="text-blue-300 text-sm font-semibold mb-2">{t('pages.credits.selectedPackage')}</div>
                <div className="text-white text-3xl font-bold mb-1">
                  {selectedPackage.credits} {t('pages.credits.credits')}
                </div>
                <div className="text-blue-200 text-sm">
                  {t('pages.credits.atPrice')} ${selectedPackage.price}
                </div>
              </div>

              <div className="w-full sm:w-auto">
                <div className="bg-zinc-900 rounded-xl p-6 mb-4 text-center">
                  <div className="text-gray-400 text-sm mb-2">{t('pages.credits.totalAmount')}</div>
                  <div className="text-white text-5xl font-bold">
                    ${selectedPackage.price}
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-5 px-8 rounded-xl transition-all flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      <span className="text-xl">{t('pages.credits.processing')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="text-xl">{t('pages.credits.payNow')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Important Information - Redesigned */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="text-center mb-6">
            <h3 className="text-white font-bold text-xl sm:text-2xl mb-2">{t('pages.credits.importantInfo')}</h3>
            <p className="text-gray-400 text-sm">{t('pages.credits.readCarefully')}</p>
          </div>
          
          {/* Key Features */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {/* Validity */}
            <div className="bg-gradient-to-br from-orange-900/20 to-orange-800/20 border-2 border-orange-500/40 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-orange-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="text-orange-200 font-bold text-lg">{t('pages.credits.validity.title')}</h4>
              </div>
              <p className="text-orange-100/80 leading-relaxed text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: t('pages.credits.validity.desc') }} />
              <p className="text-orange-200/70 text-sm mt-2">
                {t('pages.credits.validity.example')} {t('pages.credits.validity.exampleDesc')}
              </p>
            </div>

            {/* Security */}
            <div className="bg-gradient-to-br from-green-900/20 to-green-800/20 border-2 border-green-500/40 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/95 rounded-xl flex items-center justify-center flex-shrink-0 p-2">
                  <img src="/tap-icon.png" alt="Tap Payment" className="w-full h-full object-contain" />
                </div>
                <h4 className="text-green-200 font-bold text-lg">{t('pages.credits.securePayment.title')}</h4>
              </div>
              <p className="text-green-100/80 leading-relaxed text-sm sm:text-base">
                <strong className="text-green-200">{t('pages.credits.securePayment.oneTime')}</strong> {t('pages.credits.securePayment.notMonthly')}
                <br/>
                <strong className="text-green-200">{t('pages.credits.securePayment.secure')}</strong> {t('pages.credits.securePayment.viaTap')}
                <br/>
                <strong className="text-green-200">{t('pages.credits.securePayment.noCardData')}</strong> {t('pages.credits.securePayment.noCardDataDesc')}
              </p>
            </div>
          </div>

          {/* Expiry Example */}
          <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-2 border-blue-500/30 rounded-xl p-6 mb-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-blue-200 font-bold text-lg mb-2">{t('pages.credits.example.title')}</h4>
                <p className="text-blue-100/80 text-sm leading-relaxed">
                  <strong className="text-blue-200">{t('pages.credits.example.scenario')}</strong>
                  <br/>
                  {t('pages.credits.example.scenario1')}
                  <br/>
                  {t('pages.credits.example.scenario2')}
                  <br/>
                  {t('pages.credits.example.scenario3')}
                  <br/><br/>
                  <strong className="text-green-300">{t('pages.credits.example.result')}</strong>
                  <br/>
                  {t('pages.credits.example.result1')}
                  <br/>
                  {t('pages.credits.example.result2')}
                </p>
              </div>
            </div>
          </div>

          {/* Multiple Packages Notice */}
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-2 border-purple-500/40 rounded-xl p-5 text-center mb-4">
            <div className="space-y-2">
              <p className="text-purple-200 font-bold text-base sm:text-lg">
                {t('pages.credits.multiplePackages.title')}
              </p>
              <p className="text-purple-300/80 text-sm">
                {t('pages.credits.multiplePackages.reminder')}
              </p>
            </div>
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-6">
            {/* Powered By */}
            <div className="text-center md:text-right">
              <div className="text-gray-400 text-sm mb-2">{t('pages.credits.footer.poweredBy')}</div>
              <a 
                href="https://www.audome.io/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 font-semibold text-lg transition-colors"
              >
                Audome Technology
              </a>
            </div>

            {/* Contact */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.credits.footer.contact')}</div>
              <a 
                href="mailto:ahmed@audome.io" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                ahmed@audome.io
              </a>
            </div>

            {/* Social */}
            <div className="text-center md:text-left">
              <div className="text-gray-400 text-sm mb-2">{t('pages.credits.footer.follow')}</div>
              <a 
                href="https://www.instagram.com/audome.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Instagram
              </a>
            </div>

            {/* Legal */}
            <div className="text-center md:text-left">
              <div className="text-gray-400 text-sm mb-2">{t('pages.credits.footer.legal')}</div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate('/terms')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('pages.credits.footer.terms')}
                </button>
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('pages.credits.footer.privacy')}
                </button>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center text-gray-500 text-sm pt-6 border-t border-zinc-800">
            {t('pages.credits.footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CreditsPage;

